/** El marco de todas las pantallas del portal: barra lateral, contenido, paleta y papelera. */
import { Avatar } from '../componentes/piezas'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { api } from '../api'
import { CambiarTema, Salir } from '../componentes/sesion'
import { usePortal } from '../contexto'
import { EVENTO_MENCIONES } from '../paginas/Menciones'
import { PAPEL } from '../textos'
import { FilaDeFavorita, ListaDePaginas } from './Arbol'
import { useEspacio } from './contexto'
import { Menu } from './Menu'
import Paleta from './Paleta'
import Papelera from './Papelera'
import { Logo } from '../componentes/Logo'

export default function Armazon({ children }: { children: ReactNode }) {
  const { barraAbierta, alternarBarra, paletaAbierta, papeleraAbierta } = useEspacio()
  const lugar = useLocation()

  // En pantallas estrechas la barra es un cajón: al elegir un sitio, se cierra sola.
  useEffect(() => {
    if (window.innerWidth <= 900 && barraAbierta) alternarBarra()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lugar.pathname])

  return (
    <div className={`app${barraAbierta ? '' : ' app--sin-barra'}`}>
      <Barra />
      {barraAbierta && <div className="cortina" onClick={alternarBarra} aria-hidden="true" />}
      {!barraAbierta && (
        <button
          className="abrir-barra"
          onClick={alternarBarra}
          aria-label="Abrir el menú lateral"
          title="Abrir el menú (Ctrl+\)"
        >
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor"
               strokeWidth="1.6" strokeLinecap="round">
            <path d="M3 5h14M3 10h14M3 15h14" />
          </svg>
        </button>
      )}
      {children}
      {paletaAbierta && <Paleta />}
      {papeleraAbierta && <Papelera />}
    </div>
  )
}

/** El «＋» de la barra: una página en blanco o una base de datos. */
function BotonNueva() {
  const { crear } = useEspacio()
  const [abierto, setAbierto] = useState(false)
  const ancla = useRef<HTMLButtonElement>(null)
  return (
    <>
      <button
        ref={ancla}
        className="arbol-boton arbol-boton--visible"
        onClick={() => setAbierto((v) => !v)}
        aria-label="Nuevo"
        aria-haspopup="menu"
        title="Nueva página o base de datos"
      >
        ＋
      </button>
      {abierto && (
        <Menu
          ancla={ancla}
          alCerrar={() => setAbierto(false)}
          ancho={230}
          alinear="fin"
          opciones={[
            { texto: 'Página en blanco', icono: '📄', alElegir: () => void crear() },
            {
              texto: 'Base de datos',
              icono: '▦',
              alElegir: () => void crear({ kind: 'database', title: 'Base de datos' }),
            },
          ]}
        />
      )}
    </>
  )
}

function Barra() {
  const { servidor, yo, puedo } = usePortal()
  const { favoritas, alternarBarra, abrirPaleta, abrirPapelera, cargado, arbol } =
    useEspacio()
  const base = `/${servidor.id}`
  const { ruta } = usePortal()
  const lugar = useLocation()
  // Quien sólo tiene acceso a solicitudes ve una barra mínima: las solicitudes y nada más.
  const espacio = puedo('ver_espacio')

  // Cuántas menciones tiene sin leer: se pregunta al entrar, cada rato, al cambiar de pantalla y
  // cuando la bandeja avisa de que ha marcado algo.
  const [sinLeer, setSinLeer] = useState(0)
  useEffect(() => {
    let vivo = true
    const pedir = () =>
      api.get<{ sin_leer: number }>(`${ruta}/menciones/resumen`)
        .then((r) => vivo && setSinLeer(r.sin_leer))
        .catch(() => undefined)
    void pedir()
    const cada = setInterval(pedir, 45_000)
    window.addEventListener(EVENTO_MENCIONES, pedir)
    return () => {
      vivo = false
      clearInterval(cada)
      window.removeEventListener(EVENTO_MENCIONES, pedir)
    }
  }, [ruta, lugar.pathname])

  const enlaces: {
    a: string; icono: string; texto: string; visible: boolean; fin?: boolean; cuenta?: number
    color?: string
  }[] = [
    { a: base, icono: '⌂', texto: 'Inicio', visible: espacio, fin: true, color: '#2383e2' },
    { a: `${base}/solicitudes`, icono: '✉', texto: 'Solicitudes', visible: true, color: '#f97316' },
    { a: `${base}/menciones`, icono: '@', texto: 'Menciones', visible: true, cuenta: sinLeer, color: '#ec4899' },
    { a: `${base}/tareas`, icono: '☑', texto: 'Tareas', visible: espacio, color: '#16a34a' },
    { a: `${base}/proyectos`, icono: '▤', texto: 'Proyectos', visible: espacio, color: '#8b5cf6' },
    { a: `${base}/equipo`, icono: '⚑', texto: 'Equipo', visible: espacio, color: '#0ea5b7' },
    {
      a: `${base}/actividad`,
      icono: '≡',
      texto: 'Actividad',
      visible: espacio && puedo('ver_registro_de_actividad'),
      color: '#eab308',
    },
  ]

  return (
    <nav className="barra" aria-label="Secciones del portal">
      <div className="barra__cabecera">
        <Logo ancho={76} className="logo--barra" />
        <div className="barra__quien">
          <div className="barra__nombre">{servidor.nombre}</div>
          <div className="barra__titulo">Espacio de trabajo</div>
        </div>
        <button
          className="arbol-boton arbol-boton--visible"
          onClick={alternarBarra}
          aria-label="Cerrar el menú lateral"
          title="Cerrar el menú (Ctrl+\)"
        >
          «
        </button>
      </div>

      <div className="barra__nav">
        {espacio && (
          <button className="enlace" onClick={abrirPaleta}>
            <span className="enlace__icono" aria-hidden="true">⌕</span>
            Buscar
            <kbd className="enlace__atajo">Ctrl K</kbd>
          </button>
        )}
        {enlaces
          .filter((e) => e.visible)
          .map((e) => (
            <NavLink
              key={e.a}
              to={e.a}
              end={e.fin}
              className={({ isActive }) => `enlace${isActive ? ' enlace--activo' : ''}`}
            >
              <span className="enlace__icono" aria-hidden="true" style={e.color ? { color: e.color } : undefined}>{e.icono}</span>
              {e.texto}
              {!!e.cuenta && (
                <span className="enlace__cuenta" aria-label={`${e.cuenta} sin leer`}>{e.cuenta}</span>
              )}
            </NavLink>
          ))}

        {espacio && favoritas.length > 0 && (
          <section className="barra__seccion">
            <h3 className="barra__encabezado">Favoritos</h3>
            {favoritas.map((n) => (
              <FilaDeFavorita key={n.id} nodo={n} />
            ))}
          </section>
        )}

        {espacio && (
        <section className="barra__seccion" aria-label="Páginas">
          <div className="barra__encabezado barra__encabezado--accion">
            <NavLink to={`${base}/paginas`} end>Páginas</NavLink>
            {puedo('editar_paginas') && <BotonNueva />}
          </div>
          <div role="tree">
            <ListaDePaginas padre={null} nivel={0} />
          </div>
          {cargado && arbol.length === 0 && (
            <div className="arbol-vacio">
              {puedo('editar_paginas') ? 'Pulsa ＋ para crear la primera' : 'Aún no hay páginas'}
            </div>
          )}
        </section>
        )}

        {espacio && (
          <button className="enlace barra__papelera" onClick={() => abrirPapelera(true)}>
            <span className="enlace__icono" aria-hidden="true">🗑</span>
            Papelera
          </button>
        )}
      </div>

      <div className="barra__pie">
        <div className="yo">
          <Avatar nombre={yo.persona.nombre} />
          <div style={{ minWidth: 0 }}>
            <div className="yo__nombre">{yo.persona.nombre}</div>
            <div className="yo__papel">{PAPEL[yo.papel]}</div>
          </div>
        </div>
        <CambiarTema />
        <Salir />
      </div>
    </nav>
  )
}
